import React from "react";
import { useHelp } from "../../contexts/HelpContext";
import { usePricingSettings } from "../../contexts/PricingSettingsContext";

export default function HelpDrawer() {
  const {
    isOpen,
    closeHelp,
    currentHelp,
    aiAnswer,
    aiQuestion,
    aiLoading,
    aiError,
    setAiQuestion,
    askAi,
  } = useHelp();
  const { pricingSettings } = usePricingSettings();
  const aiEnabled = pricingSettings?.enableHelpAi !== false;

  return (
    <div
      className={`fixed inset-0 z-40 pointer-events-none ${
        isOpen ? "opacity-100" : "opacity-0"
      } transition-opacity duration-150`}
    >
      <div
        className={`absolute inset-0 bg-black/50 ${
          isOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
        onClick={closeHelp}
      />

      <div
        className={`absolute right-0 top-0 h-full w-full sm:w-[420px] md:w-[480px]
          transition-transform duration-200 pointer-events-auto
          ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="h-full mudo-card flex flex-col p-4 border-l border-white/10 bg-black/95">
          <header className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-base font-semibold">Help &amp; guide</h2>
              {currentHelp ? (
                <p className="text-xs opacity-70">
                  You&apos;re on: <span className="font-medium">{currentHelp.title}</span>
                </p>
              ) : (
                <p className="text-xs opacity-70">
                  Mudo can show tips and guidance for each screen.
                </p>
              )}
            </div>
            <button
              onClick={closeHelp}
              className="text-xs px-2 py-1 rounded-full border border-white/10 bg-black/40 hover:bg-black/60"
            >
              Close
            </button>
          </header>

          <div className="flex-1 overflow-auto space-y-4 text-sm">
            {currentHelp ? (
              <div>
                <p className="opacity-80">{currentHelp.summary}</p>
                {currentHelp.bullets?.length > 0 && (
                  <ul className="mt-2 text-xs opacity-80 space-y-1 list-disc list-inside">
                    {currentHelp.bullets.map((b, idx) => (
                      <li key={idx}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <p className="text-sm opacity-80">
                Help content hasn&apos;t been written for this screen yet. You can still use the tips below.
              </p>
            )}

            <div className="border border-dashed border-cyan-500/40 rounded-xl p-3 space-y-2 text-[11px]">
              <div className="flex items-center justify-between">
                <span className="uppercase tracking-wide opacity-70">Ask Mudo AI</span>
                {aiLoading && <span className="opacity-80 text-xs">Thinking…</span>}
              </div>

              <p className="opacity-80">
                Ask how to use this part of the app. For example:
                <br />
                <span className="italic">
                  “How do I set kiln capacity?” or “How do I price for wholesale with this calculator?”
                </span>
              </p>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!aiEnabled) return;
                  askAi();
                }}
                className="space-y-2"
              >
                <input
                  type="text"
                  className="w-full rounded-xl bg-[#050608] border border-white/10 px-2 py-1.5 text-[11px] text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  placeholder="Type your question…"
                  value={aiQuestion}
                  onChange={(e) => setAiQuestion(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={aiLoading || !aiQuestion.trim() || !aiEnabled}
                  className="text-[11px] px-3 py-1.5 rounded-full border border-white/10 bg-black/40 hover:bg-black/60 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ask Mudo
                </button>
              </form>

              {aiError && <p className="text-[11px] text-red-300 mt-1">{aiError}</p>}

              {aiAnswer && !aiError && (
                <div className="mt-2 rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-[11px] space-y-1">
                  <div className="opacity-70 mb-1">Mudo AI says:</div>
                  <div className="whitespace-pre-wrap opacity-90">{aiAnswer}</div>
                </div>
              )}
            </div>
          </div>

          <footer className="pt-2 mt-2 border-t border-white/5 text-[11px] opacity-60">
            Mudo&apos;s answers are guidance only. Always double-check pricing, firings and safety steps.
          </footer>
        </div>
      </div>
    </div>
  );
}
